import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, type IntegrationHealthEntry, type RecipientLocationRow, type ShipmentIntentSummary } from "../lib/admin-api";
import { getExpiryStatus } from "../lib/recipient-expiry";

export default function Dashboard() {
  const [location, setLocation] = useState<RecipientLocationRow | null>(null);
  const [dueForReview, setDueForReview] = useState(0);
  const [providers, setProviders] = useState<IntegrationHealthEntry[]>([]);
  const [intents, setIntents] = useState<ShipmentIntentSummary[]>([]);
  const [failedCount, setFailedCount] = useState(0);
  const [needsReviewCount, setNeedsReviewCount] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [tilePickerEnabled, setTilePickerEnabled] = useState<boolean | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [aiHealth, setAiHealth] = useState<{ ok: boolean; detail: string } | null>(null);
  const [checkingAi, setCheckingAi] = useState(false);

  async function refreshIntents() {
    const r = await adminApi.getShipmentIntents();
    setIntents(r.intents.slice(0, 10));
    setFailedCount(r.failedCount);
    setNeedsReviewCount(r.needsReviewCount ?? 0);
  }

  useEffect(() => {
    adminApi.listRecipientLocations().then((r) => setLocation(r.locations.find((l) => l.status === "ACTIVE") ?? null));
    adminApi.listRules({ dueForReview: "1" }).then((r) => setDueForReview(r.dueForReviewCount));
    adminApi.getIntegrationHealth().then((r) => setProviders(r.providers));
    adminApi.getSettings().then((r) => setTilePickerEnabled(r.tilePickerEnabled));
    refreshIntents();
  }, []);

  async function toggleTilePicker() {
    if (tilePickerEnabled === null) return;
    setSavingSettings(true);
    try {
      const r = await adminApi.updateSettings({ tilePickerEnabled: !tilePickerEnabled });
      setTilePickerEnabled(r.tilePickerEnabled);
    } finally {
      setSavingSettings(false);
    }
  }

  async function checkAi() {
    setCheckingAi(true);
    setAiHealth(null);
    try {
      const r = await adminApi.getAiHealth();
      setAiHealth(r);
    } catch {
      setAiHealth({ ok: false, detail: "Couldn't run the check — the admin API request itself failed." });
    } finally {
      setCheckingAi(false);
    }
  }

  return (
    <div className="stack">
      <h2>Dashboard</h2>

      {dueForReview > 0 && (
        <div className="banner banner-warn">
          <span>
            <Link to="/admin/rules">{dueForReview} rule{dueForReview === 1 ? "" : "s"} need review</Link> — never verified or older than 6 months.
          </span>
        </div>
      )}
      {needsReviewCount > 0 && (
        <div className="banner banner-danger">
          <span>
            {needsReviewCount} shipment{needsReviewCount === 1 ? "" : "s"} need manual review — payment received but fulfilment
            couldn't complete automatically, or the carrier charged a different amount than was quoted. See "Recent shipping
            intents" below.
          </span>
        </div>
      )}
      {failedCount > 0 && needsReviewCount === 0 && (
        <div className="banner banner-warn">
          <span>{failedCount} shipment{failedCount === 1 ? "" : "s"} currently in a failed state (may still be auto-retrying).</span>
        </div>
      )}

      <div className="card stack-sm">
        <strong>Current destination</strong>
        {location ? (
          <>
            <div>{location.establishment_name ?? "—"} ({formatMode(location.postal_mode)})</div>
            <div>Receiving post: {location.receiving_mail ? "Yes" : "No — paused"}</div>
            <div className="field-help">Last verified: {location.last_verified_at ? new Date(location.last_verified_at).toLocaleString("en-GB") : "Never verified"}</div>
            {(() => {
              const expiryStatus = getExpiryStatus(location);
              if (!expiryStatus) return null;
              return (
                <div className={`banner ${expiryStatus.kind === "expired" ? "banner-danger" : "banner-warn"}`} role="alert">
                  <span>{expiryStatus.message}</span>
                </div>
              );
            })()}
          </>
        ) : (
          <span className="field-help">No active location configured.</span>
        )}
        <Link to="/admin/recipient" className="btn btn-secondary" style={{ width: "fit-content" }}>
          Manage
        </Link>
      </div>

      <div className="card stack-sm">
        <strong>Wizard settings</strong>
        <label style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={tilePickerEnabled ?? false}
            disabled={tilePickerEnabled === null || savingSettings}
            onChange={toggleTilePicker}
          />
          <span>
            Show the category tile picker on the contents step
            <div className="field-help">
              On by default, alongside the free-text "describe it" AI check. Turn this off to show only the AI description box.
            </div>
          </span>
        </label>
      </div>

      <div className="card stack-sm">
        <strong>AI description helper</strong>
        <p className="field-help" style={{ margin: 0 }}>
          Sends a tiny test prompt to Anthropic's API to confirm the key is set and it's responding. Not run automatically — each
          check is a real (very small) billed API call.
        </p>
        <button className="btn btn-secondary" style={{ width: "fit-content" }} onClick={checkAi} disabled={checkingAi}>
          {checkingAi ? "Checking…" : "Check AI helper"}
        </button>
        {aiHealth && (
          <div className={`banner ${aiHealth.ok ? "banner-success" : "banner-danger"}`} role="status">
            <span>
              {aiHealth.ok ? "✓ Working — " : "✗ Not working — "}
              {aiHealth.detail}
            </span>
          </div>
        )}
      </div>

      <div className="card stack-sm">
        <strong>Carrier / integration status</strong>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Provider</th>
                <th>Status</th>
                <th>Health</th>
              </tr>
            </thead>
            <tbody>
              {providers.map((p) => (
                <tr key={p.id}>
                  <td>{p.displayName}</td>
                  <td>{p.dbStatus}</td>
                  <td>{p.health.ok ? "OK" : p.health.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Link to="/admin/integrations" className="btn btn-secondary" style={{ width: "fit-content" }}>
          Details
        </Link>
      </div>

      <div className="card stack-sm">
        <strong>Recent shipping intents</strong>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>Provider</th>
                <th>Flags</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {intents.map((i) => (
                <tr key={i.id}>
                  <td>{i.status}</td>
                  <td>{i.selected_provider_id ?? "—"}</td>
                  <td>
                    {i.needs_manual_review === 1 && <span className="tag" style={{ background: "var(--color-danger-border)" }}>REVIEW</span>}{" "}
                    {i.price_mismatch === 1 && <span className="tag" style={{ background: "var(--color-warn-border)" }}>PRICE MISMATCH</span>}{" "}
                    {i.refund_status && <span className="tag-outline tag">{i.refund_status}</span>}{" "}
                    {i.fulfilment_attempts > 0 && <span className="field-help">{i.fulfilment_attempts} attempt{i.fulfilment_attempts === 1 ? "" : "s"}</span>}
                  </td>
                  <td>{new Date(i.created_at).toLocaleString("en-GB")}</td>
                  <td>
                    {(i.status === "FAILED" || i.needs_manual_review === 1) && (
                      <div style={{ display: "flex", gap: "var(--space-2)" }}>
                        <button
                          className="btn btn-secondary"
                          style={{ width: "auto" }}
                          disabled={busyId === i.id}
                          onClick={async () => {
                            setBusyId(i.id);
                            await adminApi.retryFulfilment(i.id);
                            await refreshIntents();
                            setBusyId(null);
                          }}
                        >
                          Retry
                        </button>
                        <button
                          className="btn btn-secondary"
                          style={{ width: "auto" }}
                          disabled={busyId === i.id || i.refund_status === "REFUNDED"}
                          onClick={async () => {
                            const reason = window.prompt("Reason for refund (for the audit log):");
                            if (!reason) return;
                            setBusyId(i.id);
                            await adminApi.refundShipment(i.id, reason);
                            await refreshIntents();
                            setBusyId(null);
                          }}
                        >
                          Refund
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function formatMode(mode: string): string {
  return mode.replace(/_/g, " ").toLowerCase();
}
